import { announceFinanceAccessChange } from "@/features/giving/accessState";
import { RolePermissionSelector } from "@/features/authorization/components/RolePermissionSelector";
import { AssignmentScopeDialog } from "@/features/authorization/components/AssignmentScopeDialog";
import {
  AssignmentAccessCard,
  SelectedUserContext,
} from "@/features/authorization/components/AssignmentAccessCard";
import { useChurchTimezone } from "@/hooks/useChurchTimezone";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  createAssignment,
  createMinistry,
  createRole,
  listStaff,
  type StaffIdentity,
  listAssignmentScopes,
  type RoleAssignment,
  listAuthorizationUsers,
  listMinistries,
  listPermissions,
  listRoles,
  updateAssignment,
  updateMinistry,
  updateRole,
  type Ministry,
  type Role,
} from "@/api/authorization";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useToast } from "@/hooks/useToast";

import {
  PageContainer,
  PageHeader,
  SearchBar,
  DataTablePagination,
  LoadingState,
  EmptyState,
} from "@/design-system";
import { Badge as StatusPill } from "@/components/ui/badge";
import { Modal } from "@/components/ui/Modal";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { ConfirmDialog } from "@/design-system/dialogs/ConfirmDialog";
type Section = "ministries" | "roles" | "assignments";
const field =
  "w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white";
const tabs: [Section, string][] = [
  ["ministries", "Ministries"],
  ["roles", "Roles & Permissions"],
  ["assignments", "Role Assignments"],
];
function errorMessage(error: unknown) {
  const e = error as { response?: { data?: { detail?: string } } };
  return e.response?.data?.detail ?? "The change could not be saved.";
}
export function AuthorizationAdministrationPage({
  section,
}: {
  section: Section;
}) {
  const { churchId } = useAuth();
  const qc = useQueryClient();
  const { showToast } = useToast();
  const { permissions, isLoading: permissionsLoading } =
    useEffectivePermissions();
  const canMinistries = permissions.has("MINISTRIES_MANAGE"),
    canRoles = permissions.has("ROLES_MANAGE");
  const timeZone = useChurchTimezone(churchId, canRoles);
  const ministries = useQuery({
    queryKey: ["authorization-ministries", churchId],
    queryFn: () => listMinistries(churchId!),
    enabled: !!churchId && canMinistries,
  });
  const assignmentScopes = useQuery({
    queryKey: ["assignment-scopes", churchId],
    queryFn: () => listAssignmentScopes(churchId!),
    enabled: !!churchId && canRoles,
  });
  const [scopeAssignment, setScopeAssignment] = useState<RoleAssignment | null>(null);
  const roles = useQuery({
    queryKey: ["authorization-roles", churchId],
    queryFn: () => listRoles(churchId!),
    enabled: !!churchId && canRoles,
  });
  const permissionList = useQuery({
    queryKey: ["authorization-permissions", churchId],
    queryFn: () => listPermissions(churchId!),
    enabled: !!churchId && canRoles,
  });
  const [modal, setModal] = useState<"ministry" | "role" | "assignment" | null>(
    null,
  );
  const [staffTarget, setStaffTarget] = useState<StaffIdentity | null>(null);
  const users = useQuery({
    queryKey: ["authorization-users", churchId],
    queryFn: () => listAuthorizationUsers(churchId!),
    enabled: !!churchId && canRoles && modal === "assignment" && !staffTarget,
  });

  const usersById = useMemo(
    () => new Map(users.data?.map((user) => [user.id, user]) ?? []),
    [users.data],
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showDisabled, setShowDisabled] = useState(false);

  const assignments = useQuery({
    queryKey: ["authorization-assignments", churchId, "staff", search, page, showDisabled],
    queryFn: () => listStaff(churchId!, search, page, showDisabled),
    enabled: !!churchId && canRoles && section === "assignments",
  });
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");

  const [editingMinistry, setEditingMinistry] = useState<Ministry | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isYouthChurch, setIsYouthChurch] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [userId, setUserId] = useState("");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [scope, setScope] = useState<"church" | "ministry">("church");
  const [ministryId, setMinistryId] = useState("");
  const notify = (title: string) => showToast({ title, variant: "success" });
  const fail = (e: unknown) =>
    showToast({
      title: "Unable to save",
      description: errorMessage(e),
      variant: "error",
    });
  const ministryMutation = useMutation({
    mutationFn: () =>
      editingMinistry
        ? updateMinistry(churchId!, editingMinistry.id, {
            name,
            description,
            is_youth_church: isYouthChurch,
          })
        : createMinistry(churchId!, {
            name,
            description,
            is_youth_church: isYouthChurch,
          }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["authorization-ministries", churchId],
      });
      qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
      qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
      setModal(null);
      notify(editingMinistry ? "Ministry updated" : "Ministry created");
    },
    onError: fail,
  });
  const roleMutation = useMutation({
    mutationFn: () =>
      editingRole
        ? updateRole(churchId!, editingRole.id, {
            name,
            description,
            permission_codes: selectedPermissions,
          })
        : createRole(churchId!, {
            name,
            description,
            permission_codes: selectedPermissions,
          }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["authorization-roles", churchId] });
      qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
      qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
      setModal(null);
      notify(editingRole ? "Role updated" : "Role created");
    },
    onError: fail,
  });
  const assignmentMutation = useMutation({
    mutationFn: () =>
      Promise.allSettled(
        roleIds.map((role_id) =>
          createAssignment(churchId!, {
            user_id: userId,
            role_id,
            ministry_id: scope === "ministry" ? ministryId : null,
          }),
        ),
      ).then((results) => {
        const failure = results.find((result) => result.status === "rejected");
        if (failure?.status === "rejected") throw failure.reason;
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["authorization-assignments", churchId] });
      qc.invalidateQueries({ queryKey: ["effective-permissions", churchId] });
      if (churchId) announceFinanceAccessChange(churchId);
      qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
      qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["authorization-assignments", churchId],
      });
      qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
      qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
      setModal(null);
      notify("Role assignment saved");
    },
    onError: fail,
  });
  const toggleMinistry = (m: Ministry) =>
    updateMinistry(churchId!, m.id, { is_active: !m.is_active })
      .then(() => {
        qc.invalidateQueries({
          queryKey: ["authorization-ministries", churchId],
        });
        qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
        qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
        notify(m.is_active ? "Ministry archived" : "Ministry restored");
      })
      .catch(fail);
  const toggleRole = (r: Role) =>
    updateRole(churchId!, r.id, { is_active: !r.is_active })
      .then(() => {
        qc.invalidateQueries({ queryKey: ["authorization-roles", churchId] });
        qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
        qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
        notify(r.is_active ? "Role disabled" : "Role enabled");
      })
      .catch(fail);
  const toggleAssignment = (id: string, active: boolean) =>
    updateAssignment(churchId!, id, !active)
      .then(() => {
        qc.invalidateQueries({
          queryKey: ["authorization-assignments", churchId],
        });
        qc.invalidateQueries({ queryKey: ["ministry-contexts", churchId] });
        qc.invalidateQueries({ queryKey: ["membership-contexts", churchId] });
        qc.invalidateQueries({ queryKey: ["effective-permissions", churchId] });
        if (churchId) announceFinanceAccessChange(churchId);
        notify(active ? "Assignment disabled" : "Assignment restored");
      });
  const openMinistry = (m?: Ministry) => {
    setEditingMinistry(m ?? null);
    setIsYouthChurch(m?.is_youth_church ?? false);
    setName(m?.name ?? "");
    setDescription(m?.description ?? "");
    setModal("ministry");
  };
  const openRole = (r?: Role) => {
    setEditingRole(r ?? null);
    setName(r?.name ?? "");
    setDescription(r?.description ?? "");
    setSelectedPermissions(r?.permission_codes ?? []);
    setModal("role");
  };
  const filtered = useMemo(() => search.toLowerCase(), [search]);
  const [confirmation, setConfirmation] = useState<{
    title: string;
    action: () => Promise<unknown>;
  } | null>(null);
  const [changing, setChanging] = useState(false);
  const pending =
    ministryMutation.isPending ||
    roleMutation.isPending ||
    assignmentMutation.isPending;
  const activeQuery =
    section === "ministries"
      ? ministries
      : section === "roles"
        ? roles
        : assignments;
  const title =
    section === "ministries"
      ? "Ministries"
      : section === "roles"
        ? "Roles & Permissions"
        : "Role Assignments";
  const dependencies =
    modal === "role"
      ? [permissionList]
      : modal === "assignment"
        ? [...(staffTarget ? [] : [users]), roles, assignmentScopes]
        : [];
  const optionsUnavailable = dependencies.some((q) => q.isPending || q.isError);
  const ministriesRows =
    ministries.data?.filter(
      (m) =>
        (m.name + " " + (m.description ?? ""))
          .toLowerCase()
          .includes(filtered) &&
        (statusFilter === "all" || (statusFilter === "active") === m.is_active),
    ) ?? [];
  const rolesRows =
    roles.data?.filter(
      (r) =>
        (r.name + " " + (r.description ?? ""))
          .toLowerCase()
          .includes(filtered) &&
        (statusFilter === "all" || (statusFilter === "active") === r.is_active),
    ) ?? [];
  const total = section === "ministries" ? ministriesRows.length
    : section === "roles" ? rolesRows.length : assignments.data?.total ?? 0;
  const currentPage = section === "assignments" ? assignments.data?.page ?? page
    : Math.min(page, Math.max(1, Math.ceil(total / 25)));
  if (permissionsLoading)
    return (
      <PageContainer>
        <LoadingState message="Loading administration…" />
      </PageContainer>
    );
  if (
    (section === "ministries" && !canMinistries) ||
    (section !== "ministries" && !canRoles)
  )
    return (
      <PageContainer>
        <EmptyState
          title="Administration access required"
          description="Your current assignments do not grant permission to manage this area."
        />
      </PageContainer>
    );
  return (
    <PageContainer>
      <PageHeader
        title={title}
        subtitle="Administration · Manage ministry structure and church-user access."
        actions={
          <Button
            onClick={() => {
              if (section === "ministries") openMinistry();
              else if (section === "roles") openRole();
              else {
                setStaffTarget(null);
                setUserId("");
                setRoleIds([]);
                setScope("church");
                setMinistryId("");
                setModal("assignment");
              }
            }}
          >
            <span className="flex items-center gap-2">
              <Plus size={16} />
              {section === "assignments"
                ? "Assign roles"
                : section === "roles"
                  ? "Create role"
                  : "Create ministry"}
            </span>
          </Button>
        }
      />
      <nav
        aria-label="Administration"
        className="flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2"
      >
        {tabs
          .filter(([key]) => (key === "ministries" ? canMinistries : canRoles))
          .map(([key, label]) => (
            <NavLink
              key={key}
              to={`/settings/administration/${key}`}
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setPage(1);
              }}
              className={({ isActive }) =>
                `rounded-xl px-4 py-2 text-sm font-semibold ${isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800"}`
              }
            >
              {label}
            </NavLink>
          ))}
      </nav>
      {section === "assignments" && (
        <Card className="p-5 text-sm text-slate-400">
          Manage login access from the member profile. Role assignments control
          access to the entire church or a specific ministry, independently of
          attendee membership.
        </Card>
      )}
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <SearchBar
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={`Search ${title.toLowerCase()}`}
          />
        </div>
        {section === "assignments" ? <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={showDisabled} onChange={(e) => { setShowDisabled(e.target.checked); setPage(1); }} />
          Show disabled/history
        </label> : <select
          aria-label="Status filter"
          className={`${field} sm:w-44`}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as "all" | "active" | "inactive");
            setPage(1);
          }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>}
      </Card>
      {activeQuery.isPending && (
        <LoadingState message={`Loading ${title.toLowerCase()}…`} />
      )}
      {activeQuery.isError && (
        <QueryErrorState
          title={`Unable to load ${title.toLowerCase()}`}
          onRetry={() => void activeQuery.refetch()}
        />
      )}
      {section === "ministries" && (
        <div className="grid gap-3">
          {ministriesRows
            .slice((currentPage - 1) * 25, currentPage * 25)
            .map((m) => (
              <Card
                key={m.id}
                className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 break-words">
                  <div className="flex flex-wrap gap-2">
                    <h2 className="font-semibold">{m.name}</h2>
                    <Badge active={m.is_active} />
                  </div>
                  <p className="text-sm text-slate-400">
                    {m.description || "No description"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openMinistry(m)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant={m.is_active ? "danger" : "secondary"}
                    size="sm"
                    onClick={() =>
                      setConfirmation({
                        title: `${m.is_active ? "Archive" : "Restore"} ${m.name}?`,
                        action: () => toggleMinistry(m),
                      })
                    }
                  >
                    {m.is_active ? "Archive" : "Restore"}
                  </Button>
                </div>
              </Card>
            ))}
        </div>
      )}
      {section === "roles" && (
        <div className="grid gap-3">
          {rolesRows
            .slice((currentPage - 1) * 25, currentPage * 25)
            .map((r) => (
              <Card key={r.id} className="p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 break-words">
                    <div className="flex flex-wrap gap-2">
                      <h2 className="font-semibold">{r.name}</h2>
                      <Badge active={r.is_active} />
                      {r.is_system && (
                        <StatusPill variant="info">System role</StatusPill>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-400">
                      {r.description || "No description"}
                    </p>
                    <p className="mt-3 text-xs text-slate-500">
                      {r.permission_codes.length} permissions
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={r.system_code === "FINANCE"}
                      onClick={() => openRole(r)}
                    >
                      Edit
                    </Button>
                    {!r.is_system && (
                      <Button
                        variant={r.is_active ? "danger" : "secondary"}
                        size="sm"
                        onClick={() =>
                          setConfirmation({
                            title: `${r.is_active ? "Disable" : "Enable"} ${r.name}?`,
                            action: () => toggleRole(r),
                          })
                        }
                      >
                        {r.is_active ? "Disable" : "Enable"}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
        </div>
      )}
      {section === "assignments" && (
        <div className="grid gap-3">
          {assignments.data?.items.map((person) => (
            <Card key={person.id} className="min-w-0 space-y-4 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <SelectedUserContext user={person} timeZone={timeZone} />
                <Button variant="secondary" disabled={!person.is_active || changing} onClick={() => {
                  setStaffTarget(person); setUserId(person.id); setRoleIds([]);
                  setScope("church"); setMinistryId(""); setModal("assignment");
                }}>Assign Role</Button>
              </div>
              <h3 className="text-sm font-semibold">Roles / Assignments</h3>
              {!showDisabled && person.disabled_assignment_count > 0 && <p className="text-sm text-slate-400">
                {person.disabled_assignment_count} disabled assignment(s) hidden. Use Show disabled/history to review or restore.
              </p>}
              {!person.assignments.length && <p className="text-sm text-slate-400">No {showDisabled ? "" : "active "}role assignments.</p>}
              {person.assignments.map((a) => <AssignmentAccessCard
                key={a.id} assignment={a} nested pending={changing}
                onEditScope={() => setScopeAssignment(a)}
                onToggle={() => setConfirmation({
                  title: `${a.is_active ? "Disable" : "Restore"} role assignment?`,
                  action: () => toggleAssignment(a.id, a.is_active),
                })}
              />)}
            </Card>
          ))}
        </div>
      )}
      {!activeQuery.isPending && !activeQuery.isError && !total && (
        <EmptyState
          title={`No ${title.toLowerCase()} found`}
          description="Try adjusting your search or status filter, or add a new record."
        />
      )}
      {section === "assignments" && !assignments.isPending && !assignments.isError && <p className="text-sm text-slate-400">{total} staff identities · 25 people per page</p>}
      {total > 25 && (
        <DataTablePagination
          page={currentPage}
          canGoNext={currentPage * 25 < total}
          onPrevious={() => setPage(currentPage - 1)}
          onNext={() => setPage(currentPage + 1)}
        />
      )}
      {scopeAssignment && churchId && <AssignmentScopeDialog churchId={churchId} assignment={scopeAssignment} onClose={() => setScopeAssignment(null)} />}
      <ConfirmDialog
        open={!!confirmation}
        title={confirmation?.title ?? "Confirm change"}
        description="This changes availability or access. Existing records and history are preserved."
        pending={changing}
        onCancel={() => setConfirmation(null)}
        onConfirm={async () => {
          if (!confirmation || changing) return;
          setChanging(true);
          try {
            await confirmation.action();
            setConfirmation(null);
          } catch (error) {
            fail(error);
          } finally {
            setChanging(false);
          }
        }}
      />
      {modal && (
        <Modal
          open
          onClose={() => {
            if (!pending) setModal(null);
          }}
          ariaLabelledBy="admin-dialog-title"
          panelClassName={modal === "role" ? "max-w-3xl" : "max-w-2xl"}
        >
          <h2 id="admin-dialog-title" className="text-xl font-semibold">
            {modal === "ministry"
              ? `${editingMinistry ? "Edit" : "Create"} ministry`
              : modal === "role"
                ? `${editingRole ? "Edit" : "Create"} role`
                : "Assign roles"}
          </h2>
          {modal !== "assignment" && (
            <div className="mt-5 space-y-3">
              <label className="block space-y-2 text-sm">
                <span>Name</span>
                <input
                  className={field}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </label>
              <label className="block space-y-2 text-sm">
                <span>Description</span>
                <textarea
                  rows={3}
                  className={field}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </label>
            </div>
          )}
          {modal === "ministry" && (
            <label className="mt-4 block text-sm">
              <input
                type="checkbox"
                checked={isYouthChurch}
                disabled={editingMinistry?.is_youth_church}
                onChange={(e) => setIsYouthChurch(e.target.checked)}
              />{" "}
              Designate as Youth Ministry (defines attendee eligibility; cannot
              be cleared)
            </label>
          )}
          {dependencies.some((q) => q.isPending) && (
            <LoadingState message="Loading options…" />
          )}
          {dependencies.some((q) => q.isError) && (
            <QueryErrorState
              title="Unable to load options"
              onRetry={() => dependencies.forEach((q) => void q.refetch())}
            />
          )}
          {modal === "role" && (
            <RolePermissionSelector
              permissions={(permissionList.data ?? []).filter(p => !p.code.startsWith("GIVING_"))}
              selectedPermissions={selectedPermissions}
              onToggle={(code) =>
                setSelectedPermissions((v) =>
                  v.includes(code) ? v.filter((x) => x !== code) : [...v, code],
                )
              }
            />
          )}
          {modal === "assignment" && (
            <div className="mt-5 space-y-4">
              <select
                aria-label="Person"
                className={field}
                disabled={!!staffTarget || pending}
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              >
                <option value="">Select person</option>
                {(staffTarget ? [staffTarget] : users.data)?.map((u) => (
                  <option key={u.id} disabled={!u.is_active} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                    {!u.is_active ? " — inactive" : ""}
                  </option>
                ))}
              </select>
              <SelectedUserContext
                user={staffTarget ?? usersById.get(userId)}
                timeZone={timeZone}
              />
              <div>
                <p className="mb-2 text-sm text-slate-400">
                  Select one or more active roles. Finance grants financial access only at Entire church scope; Church Administrators must assign it explicitly.
                </p>
                {roles.data
                  ?.filter((r) => r.is_active && (scope === "church" || r.system_code !== "FINANCE"))
                  .map((r) => (
                    <label
                      key={r.id}
                      className="mr-4 inline-flex gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={roleIds.includes(r.id)}
                        onChange={() =>
                          setRoleIds((v) =>
                            v.includes(r.id)
                              ? v.filter((x) => x !== r.id)
                              : [...v, r.id],
                          )
                        }
                      />
                      {r.name}
                    </label>
                  ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <label>
                  <input
                    type="radio"
                    checked={scope === "church"}
                    onChange={() => setScope("church")}
                  />{" "}
                  Entire church
                </label>
                <label>
                  <input
                    type="radio"
                    checked={scope === "ministry"}
                    onChange={() => { setScope("ministry"); setRoleIds(ids => ids.filter(id => roles.data?.find(r => r.id === id)?.system_code !== "FINANCE")); }}
                  />{" "}
                  Ministry-specific
                </label>
              </div>
              {scope === "ministry" && (
                <select
                  aria-label="Ministry"
                  className={field}
                  value={ministryId}
                  onChange={(e) => setMinistryId(e.target.value)}
                >
                  <option value="">Select active ministry</option>
                  {assignmentScopes.data
                    ?.filter((m) => m.is_active)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </select>
              )}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => setModal(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={
                pending ||
                optionsUnavailable ||
                (modal === "assignment"
                  ? !userId ||
                    !roleIds.length ||
                    (scope === "ministry" && !ministryId)
                  : !name.trim())
              }
              onClick={() =>
                modal === "ministry"
                  ? ministryMutation.mutate()
                  : modal === "role"
                    ? roleMutation.mutate()
                    : assignmentMutation.mutate()
              }
            >
              {pending ? "Saving…" : "Save"}
            </Button>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
}
function Badge({ active }: { active: boolean }) {
  return (
    <StatusPill variant={active ? "success" : "neutral"}>
      {active ? "Active" : "Inactive"}
    </StatusPill>
  );
}
